import { unwrapResult } from "@reduxjs/toolkit";
import React, { useEffect, useMemo, useRef, useState } from "react";
import { format } from "date-fns";
import { useDispatch, useSelector } from "react-redux";
import { InputAdornment } from "@mui/material";
import { LocalizationProvider } from "@mui/x-date-pickers";
import { AdapterDateFns } from "@mui/x-date-pickers/AdapterDateFnsV3";
import { fr, enUS, es } from "date-fns/locale";
import { ApiService } from "../../../../api";
import {
	CustomDatePicker,
	CustomDialog,
	CustomTextField,
	icon,
	IconComponent,
	LimitedTextField,
} from "../../../../common/components";
import { translate } from "../../../../common/providers";
import { createFolder, renameFolder, getTree, updateFile, renameFile } from "../../slice/document-center-slice";
import styles from "./SaveFileDialog.module.css";
import { isDeepEqual } from "../../../../common/utils";

function getValidDate(input) {
	const date = new Date(input);
	return Number.isNaN(date.getTime()) ? null : date;
}

const ATTRIBUTES = {
	NUMBER: "Numéro",
	DATE: "Date d'effet du texte",
	NATURE: "Nature du texte contractuel",
	TYPE: "Type de texte",
};

const config = {
	folder: {
		create: {
			label: "document-center.save-dialog.folder.label",
			submitLabel: "common:btn.add",
			title: "document-center.save-dialog.title.new",
		},
		rename: {
			label: "document-center.save-dialog.folder.label",
			submitLabel: "common:btn.save",
			title: "document-center.save-dialog.title.rename",
		},
	},
	file: {
		rename: {
			label: "document-center.save-dialog.file.label",
			submitLabel: "common:btn.save",
			title: "document-center.save-dialog.title.rename",
		},
		create: {},
	},
};

export default function SaveFileDialog({
	fileId = null,
	isEditingMetadata,
	onClose,
	open,
	type = "folder",
	value = "",
}) {
	const [tempValue, setTempValue] = useState(value);
	const dispatch = useDispatch();
	const projectId = useSelector(({ context }) => context.project.id);
	const language = useSelector(({ context }) => context.language.value);
	const cancelTokenRef = useRef(null);
	const dialogConfig = useMemo(() => config[type][fileId && value ? "rename" : "create"], [fileId, type, value]);
	const localeLanguage = useMemo(
		() => (language === "en" && enUS) || (language === "fr" && fr) || (language === "esp" && es) || enUS,
		[language]
	);
	useEffect(() => {
		cancelTokenRef.current = ApiService.getCancelTokenSource();
		return () => {
			ApiService.cancelTokens(cancelTokenRef.current);
		};
	}, []);
	useEffect(() => {
		if (open) {
			setTempValue(value);
		}
	}, [value, open]);
	const handleClose = () => {
		setTempValue(value);
		onClose();
	};
	const handleSubmit = () => {
		const { token } = cancelTokenRef.current;
		if (type === "folder") {
			if (fileId && value) {
				dispatch(renameFolder({ projectId, name: tempValue.label, directoryId: fileId, token }));
			} else {
				dispatch(createFolder({ projectId, label: tempValue.label, token }))
					.then(unwrapResult)
					.then(() => {
						dispatch(getTree({ projectId, token: ApiService.getCancelTokenSource().token }));
					});
			}
		} else {
			if (value.label !== tempValue.label) {
				dispatch(renameFile({ docId: fileId, name: tempValue.label, token }));
			}
			if (!isDeepEqual(value.documentAttributes, tempValue.documentAttributes)) {
				dispatch(
					updateFile({ projectId, docId: fileId, documentAttributes: tempValue.documentAttributes, token })
				);
			}
		}
		handleClose();
	};

	if (!dialogConfig) {
		throw Error(`No config found for: ${type} / ${fileId && value ? "rename" : "create"}`);
	}
	const handleChange = (e) => {
		setTempValue((prev) => ({ ...prev, label: e.target.value }));
	};
	const handleGlobalUpdate = ({ uuid, value: newValue }) => {
		const elementExist = value.documentAttributes?.findIndex((attribute) => attribute.attributeKey === uuid) >= 0;
		if (uuid === ATTRIBUTES.NUMBER) {
			if (
				tempValue.documentAttributes?.findIndex((attribute) => attribute.attributeKey === ATTRIBUTES.NUMBER) >=
				0
			) {
				if (!elementExist && newValue === "") {
					setTempValue((prev) => ({
						...prev,
						documentAttributes: tempValue.documentAttributes.filter(
							(attribute) => attribute.attributeKey !== ATTRIBUTES.NUMBER
						),
					}));
				} else {
					setTempValue((prev) => ({
						...prev,
						documentAttributes: tempValue.documentAttributes.map((attribute) => {
							if (attribute.attributeKey === ATTRIBUTES.NUMBER) {
								return {
									...attribute,
									attributeValue: newValue,
									toBeRemoved: (newValue === "" && elementExist) || undefined,
								};
							}
							return attribute;
						}),
					}));
				}
			} else {
				setTempValue((prev) => ({
					...prev,
					documentAttributes: [
						...prev.documentAttributes,
						{
							attributeKey: ATTRIBUTES.NUMBER,
							attributeValue: newValue,
							documentId: fileId,
							attributeType: "INTEGER",
						},
					],
				}));
			}
		}
		if (uuid === ATTRIBUTES.DATE) {
			const formatedDate =
				(getValidDate(newValue) && format(newValue, "yyyy-MM-dd HH:mm:ss")?.substring(0, 10)) || "";
			if (
				tempValue.documentAttributes?.findIndex((attribute) => attribute.attributeKey === ATTRIBUTES.DATE) >= 0
			) {
				if (!elementExist && formatedDate === "") {
					setTempValue((prev) => ({
						...prev,
						documentAttributes: tempValue.documentAttributes.filter(
							(attribute) => attribute.attributeKey !== ATTRIBUTES.DATE
						),
					}));
				} else {
					setTempValue((prev) => ({
						...prev,
						documentAttributes: tempValue.documentAttributes.map((attribute) => {
							if (attribute.attributeKey === ATTRIBUTES.DATE) {
								return {
									...attribute,
									attributeValue: formatedDate,
									toBeRemoved: (formatedDate === "" && elementExist) || undefined,
								};
							}
							return attribute;
						}),
					}));
				}
			} else {
				setTempValue((prev) => ({
					...prev,
					documentAttributes: [
						...prev.documentAttributes,
						{
							attributeKey: ATTRIBUTES.DATE,
							attributeValue: formatedDate,
							documentId: fileId,
							attributeType: "DATE",
						},
					],
				}));
			}
		}
		if (uuid === ATTRIBUTES.NATURE) {
			if (
				tempValue.documentAttributes?.findIndex((attribute) => attribute.attributeKey === ATTRIBUTES.NATURE) >=
				0
			) {
				setTempValue((prev) => ({
					...prev,
					documentAttributes: tempValue.documentAttributes.map((attribute) => {
						if (attribute.attributeKey === ATTRIBUTES.NATURE) {
							return {
								...attribute,
								attributeValue: newValue,
								toBeRemoved: (newValue === "" && elementExist) || undefined,
							};
						}
						return attribute;
					}),
				}));
			} else {
				setTempValue((prev) => ({
					...prev,
					documentAttributes: [
						...prev.documentAttributes,
						{
							attributeKey: ATTRIBUTES.NATURE,
							attributeValue: newValue,
							documentId: fileId,
							attributeType: "TEXT",
						},
					],
				}));
			}
		}
		if (uuid === ATTRIBUTES.TYPE) {
			if (
				tempValue.documentAttributes?.findIndex((attribute) => attribute.attributeKey === ATTRIBUTES.TYPE) >= 0
			) {
				setTempValue((prev) => ({
					...prev,
					documentAttributes: tempValue.documentAttributes.map((attribute) => {
						if (attribute.attributeKey === ATTRIBUTES.TYPE) {
							return {
								...attribute,
								attributeValue: newValue,
								toBeRemoved: (newValue === "" && elementExist) || undefined,
							};
						}
						return attribute;
					}),
				}));
			} else {
				setTempValue((prev) => ({
					...prev,
					documentAttributes: [
						...prev.documentAttributes,
						{
							attributeKey: ATTRIBUTES.TYPE,
							attributeValue: newValue,
							documentId: fileId,
							attributeType: "TEXT",
						},
					],
				}));
			}
		}
	};
	return (
		<>
			<CustomDialog
				open={open}
				submitLabel={translate(dialogConfig.submitLabel)}
				title={
					isEditingMetadata
						? translate("document-center.save-dialog.title.modify")
						: translate(dialogConfig.title)
				}
				onClose={handleClose}
				onSubmit={handleSubmit}
			>
				{!isEditingMetadata && (
					<CustomTextField
						autoFocus
						fullWidth
						required
						className={styles.textField}
						label={translate(dialogConfig.label)}
						size="small"
						value={tempValue?.label}
						variant="outlined"
						onChange={handleChange}
					/>
				)}
				{isEditingMetadata && (
					<div>
						<div>
							<span className={styles.inputLabel}>
								{translate("document-center.table.column.number")}
							</span>
							<CustomTextField
								fullWidth
								className={styles.input}
								InputProps={{
									endAdornment: (
										<InputAdornment position="end">
											<IconComponent color="var(--grey-400)" icon={icon.faHashtag} />
										</InputAdornment>
									),
									style: {
										fontSize: "var(--font-size-sm)",
										height: 32,
									},
								}}
								placeholder={translate("document-center.save-dialog.placeholder.number")}
								type="number"
								value={
									tempValue.documentAttributes?.find(
										(attr) => attr.attributeKey === ATTRIBUTES.NUMBER
									)?.attributeValue || ""
								}
								onChange={(e) => handleGlobalUpdate({ uuid: ATTRIBUTES.NUMBER, value: e.target.value })}
							/>
						</div>
						<div>
							<span className={styles.inputLabel}>
								{translate("document-center.table.column.effective-date")}
							</span>
							<LocalizationProvider adapterLocale={localeLanguage} dateAdapter={AdapterDateFns}>
								<CustomDatePicker
									fullWidth
									className={styles.datePicker}
									inputFormat="dd-MM-yyyy"
									renderInput={(params) => <CustomTextField placeholder="tt.mm.jjjj" {...params} />}
									value={getValidDate(
										tempValue.documentAttributes?.find(
											(attr) => attr.attributeKey === ATTRIBUTES.DATE
										)?.attributeValue || ""
									)}
									onChange={(e) => handleGlobalUpdate({ uuid: ATTRIBUTES.DATE, value: e })}
								/>{" "}
							</LocalizationProvider>

							<span className={styles.helperText}>
								{translate("document-center.save-dialog.placeholder.date")}
							</span>
						</div>
						<div>
							<span className={styles.inputLabel}>
								{translate("document-center.table.column.nature")}
							</span>
							<LimitedTextField
								fullWidth
								multiline
								className={styles.multiLineInput}
								placeholder={translate("document-center.save-dialog.placeholder.nature")}
								rows={5}
								value={
									tempValue.documentAttributes?.find(
										(attr) => attr.attributeKey === ATTRIBUTES.NATURE
									)?.attributeValue || ""
								}
								onChange={(e) => handleGlobalUpdate({ uuid: ATTRIBUTES.NATURE, value: e })}
							/>
						</div>
						<div>
							<span className={styles.inputLabel}>
								{translate("document-center.table.column.type-of-text")}
							</span>
							<LimitedTextField
								fullWidth
								className={styles.input}
								maxLength={150}
								placeholder={translate("document-center.save-dialog.placeholder.type")}
								value={
									tempValue.documentAttributes?.find((attr) => attr.attributeKey === ATTRIBUTES.TYPE)
										?.attributeValue || ""
								}
								onChange={(e) => handleGlobalUpdate({ uuid: ATTRIBUTES.TYPE, value: e })}
							/>
						</div>
					</div>
				)}
			</CustomDialog>
		</>
	);
}
